import { copyToClipboard } from '@/utils/clipboard'
import { downloadFile } from '@/utils/download'
import { isMobileDevice } from '@/utils/device'

type StorageKey =
  | 'authRedirect'
  | 'teamInvitation'
  | 'passwordReset'
  | 'deviceToken'
  | 'installPromptDismissed'
  | 'eventFilters'

export interface ConfirmOptions {
  title?: string
  message: string
  okButtonTitle?: string
  cancelButtonTitle?: string
}

export interface AppState {
  isActive: boolean
}

const baseShareUrl = import.meta.env.PROD
  ? (import.meta.env.VITE_PUBLIC_BASE_URL as string)
  : location.origin

/**
 * Prompt a confirm dialog using the native browser confirm.
 */
export async function confirm(options: ConfirmOptions): Promise<boolean> {
  return window.confirm(options.message)
}

/**
 * Key-value storage utilizing localStorage.
 */
export async function setStorageItem(
  key: StorageKey,
  value: string
): Promise<void> {
  localStorage.setItem(key, value)
}

export async function getStorageItem(key: StorageKey): Promise<string | null> {
  return localStorage.getItem(key)
}

export async function removeStorageItem(key: StorageKey): Promise<void> {
  localStorage.removeItem(key)
}

/**
 * Listens to application focus/visibility state using Page Visibility API.
 */
export function setAppStateListener(
  onAppStateChange: (state: AppState) => void
): void {
  document.addEventListener('visibilitychange', () => {
    onAppStateChange({
      isActive: document.visibilityState === 'visible'
    })
  })
}

/**
 * Share a URL via Web Share API or copy it to clipboard as a fallback.
 *
 * Resolves to 'share' if native share sheet was used, 'copy' if copied to clipboard,
 * or 'abort' if the user explicitly cancelled the share sheet.
 * Throws an error on failure.
 */
export async function shareUrl(options: {
  title: string
  url: string
  text?: string
}): Promise<'share' | 'copy' | 'abort'> {
  const shareableUrl = options.url.startsWith('/')
    ? baseShareUrl + options.url
    : options.url

  if (navigator.share && isMobileDevice()) {
    try {
      await navigator.share({
        title: options.title,
        text: options.text,
        url: shareableUrl
      })
      return 'share'
    } catch (error: any) {
      if (error?.name === 'AbortError') {
        // User cancelled the share dialog
        return 'abort'
      }
      console.warn('Web Share failed, falling back to copy:', error)
    }
  }

  const copied = await copyToClipboard(shareableUrl)
  if (copied) {
    return 'copy'
  }
  throw new Error('Clipboard copy failed')
}

/**
 * Share or download a generated file content (like CSV/Text).
 *
 * Resolves to 'share' if native share sheet was used, 'download' if downloaded,
 * or 'abort' if the user explicitly cancelled the share sheet.
 * Throws an error on failure.
 */
export async function shareFile(
  fileContent: string,
  contentType: string,
  fileName: string
): Promise<'share' | 'download' | 'abort'> {
  if (navigator.share && isMobileDevice()) {
    try {
      const blob = new Blob([fileContent], { type: contentType })
      const file = new File([blob], fileName, { type: contentType })
      const files = [file]

      if (navigator.canShare?.({ files })) {
        await navigator.share({
          title: fileName,
          files
        })
        return 'share'
      }
    } catch (error: any) {
      if (error?.name === 'AbortError') {
        return 'abort'
      }
      console.warn('Web Share failed, falling back to download:', error)
    }
  }

  const isDownloaded = downloadFile(fileContent, contentType, fileName)

  if (isDownloaded) {
    return 'download'
  }
  throw new Error('Failed to share or download file')
}
